'use client';

import { useRedirectTry } from '@/app/[locale]/_components/use-redirect-try';
import { HOME_PAGE } from '@/app/constants';
import { generateAnchorId } from '@/app/utils';

interface AnchorLinkProps {
  href: string;
  children: React.ReactNode;
  className?: string;
  target?: string;
  rel?: string;
}

const SCROLL_SETTLE_TIMEOUT_MS = 800;
const SCROLL_CORRECTION_THRESHOLD_PX = 2;
const SCROLL_MAX_CORRECTIONS = 3;

// 把目标元素之前的懒加载图片提前到 eager，避免 smooth scroll 途中
// 才被触发加载、撑开高度导致最终落点偏在标题上方。
function eagerLoadImagesAbove(targetTop: number) {
  const imgs = document.querySelectorAll<HTMLImageElement>('img[loading="lazy"]');
  imgs.forEach((img) => {
    const imgTop = img.getBoundingClientRect().top + window.scrollY;
    if (imgTop < targetTop) {
      img.loading = 'eager';
    }
  });
}

// 等待一次 scroll 序列结束：优先用原生 `scrollend`，老浏览器 fallback 到 timeout。
function onceScrollSettled(callback: () => void) {
  let done = false;
  const finish = () => {
    if (done) return;
    done = true;
    window.removeEventListener('scrollend', finish);
    clearTimeout(timeoutId);
    callback();
  };
  window.addEventListener('scrollend', finish, { once: true });
  const timeoutId = window.setTimeout(finish, SCROLL_SETTLE_TIMEOUT_MS);
}

function scrollToAnchor(targetElement: HTMLElement) {
  const computeTop = () => {
    const header = document.querySelector<HTMLElement>('header');
    const breadcrumbs = document.querySelector<HTMLElement>('[data-blog-breadcrumbs]');
    const offset = (header?.offsetHeight ?? 0) + (breadcrumbs?.offsetHeight ?? 0) + 16;
    return Math.max(0, targetElement.getBoundingClientRect().top + window.scrollY - offset);
  };

  eagerLoadImagesAbove(targetElement.getBoundingClientRect().top + window.scrollY);

  let corrections = 0;
  const scrollOnce = (smooth: boolean) => {
    window.scrollTo({ behavior: smooth ? 'smooth' : 'auto', top: computeTop() });
    onceScrollSettled(() => {
      if (corrections >= SCROLL_MAX_CORRECTIONS) return;
      const refinedTop = computeTop();
      if (Math.abs(refinedTop - window.scrollY) > SCROLL_CORRECTION_THRESHOLD_PX) {
        corrections += 1;
        scrollOnce(false);
      }
    });
  };

  scrollOnce(true);
}

export default function AnchorLink({ href, children, className, target, rel }: AnchorLinkProps) {
  const handleRedirect = useRedirectTry();

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (href?.startsWith('#')) {
      e.preventDefault();
      const targetId = generateAnchorId(href.slice(1));
      const targetElement = document.getElementById(targetId);

      if (targetElement) {
        scrollToAnchor(targetElement);
      }
    } else if (href.includes(HOME_PAGE)) {
      e.preventDefault();
      handleRedirect(HOME_PAGE, 'blog - create');
    }
  };

  return (
    <a
      href={href}
      onClick={handleClick}
      className={className}
      rel={rel}
      target={target}
      data-google-interstitial="false"
    >
      {children}
    </a>
  );
}
